import React from "react";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { cn, fmtDate, publicAppUrl, whatsappShareUrl } from "../lib/api";

/**
 * WhatsApp Share button for a match poll.
 * Uses an <a href="https://wa.me/…" target="_blank"> which works in restricted
 * iframes / sandboxes where window.open() may be blocked.
 * Falls back to copying the message to clipboard.
 */
export default function PollWhatsAppShare({
  poll,
  teams,
  variant = "primary",
  size = "md",
  className,
  label = "Share on WhatsApp",
  "data-testid": testid = "poll-share-whatsapp",
}) {
  const buildMessage = () => {
    const parts = [];
    
    // Title with cricket emoji
    parts.push(`🏏 *${poll.title}*`);
    
    // Match type
    if (poll.match_type) {
      const matchType = String(poll.match_type).toLowerCase();
      const capitalized = matchType.charAt(0).toUpperCase() + matchType.slice(1);
      parts.push(`Match Type: ${capitalized}`);
    }
    
    // Date and time range (e.g., "25 Jul 2026 • 06:00–09:00")
    const dateStr = fmtDate(poll.date);
    
    // Helper to normalize time strings to HH:MM
    const normalizeTime = (t) => {
      if (!t) return null;
      if (typeof t !== "string") return null;
      const trimmed = t.trim();
      if (trimmed.length >= 5) return trimmed.substring(0, 5);
      return trimmed;
    };
    
    const startTime = normalizeTime(poll.start_time);
    const endTime = normalizeTime(poll.end_time);
    const legacyTime = normalizeTime(poll.time);
    
    if (startTime && endTime) {
      parts.push(`📅 ${dateStr} • ${startTime}–${endTime}`);
    } else if (startTime) {
      parts.push(`📅 ${dateStr} • ${startTime}`);
    } else if (legacyTime) {
      parts.push(`📅 ${dateStr} • ${legacyTime}`);
    } else {
      parts.push(`📅 ${dateStr}`);
    }
    
    // Ground
    if (poll.ground) {
      parts.push(`📍 ${poll.ground}`);
    }
    
    // Ground location
    if (poll.ground_location) {
      parts.push(`🗺️ ${poll.ground_location}`);
    }
    
    // Match fee if applicable
    if (poll.match_fee > 0) {
      parts.push(`💸 ₹${poll.match_fee}/head`);
    }
    
    // Teams section — only when the caller passes teams with names resolved;
    // poll.teams itself holds member ids, so a plain poll share is the availability invite
    const teamsData = teams;
    if (teamsData) {
      // Helper to resolve a player entry (member ID or name) to a display name; guests are marked
      const resolveName = (player) => {
        if (!player) return "—";
        if (typeof player === "string") return player;
        const name = player.name || player.id || "—";
        return player.guest ? `${name} (Guest)` : name;
      };

      const teamA = (teamsData.team_a || []).map(resolveName);
      const teamB = (teamsData.team_b || []).map(resolveName);
      const captainA = resolveName(teamsData.captain_a);
      const captainB = resolveName(teamsData.captain_b);
      const vcA = resolveName(teamsData.vc_a);
      const vcB = resolveName(teamsData.vc_b);

      parts.push("");
      parts.push("*Team A*");
      parts.push(`Captain: ${captainA}`);
      if (vcA && vcA !== captainA) parts.push(`Vice Captain: ${vcA}`);
      if (teamA.length > 0) {
        teamA.forEach((player, index) => {
          parts.push(`${index + 1}. ${player}`);
        });
      }
      
      parts.push("");
      parts.push("*Team B*");
      parts.push(`Captain: ${captainB}`);
      if (vcB && vcB !== captainB) parts.push(`Vice Captain: ${vcB}`);
      if (teamB.length > 0) {
        teamB.forEach((player, index) => {
          parts.push(`${index + 1}. ${player}`);
        });
      }
      
      // Odd number of players: one plays for both sides
      if (teamsData.common) {
        parts.push("");
        parts.push(`Common: ${resolveName(teamsData.common)}`);
      }
    }
    
    // Only add the poll link / CTA if we are NOT sharing teams
    if (!teamsData) {
      parts.push("");
      parts.push("Tap to confirm availability 👇");
      const link = `${publicAppUrl()}/polls/${poll.id}`;
      parts.push(link);
    }
    
    parts.push("");
    parts.push("Thanks,");
    parts.push("Cricso");
    parts.push("Powered by HMT Technologies");
    
    return parts.join("\n");
  };

  const text = buildMessage();
  
  // Use encodeURIComponent for proper encoding
  const whatsappUrl = whatsappShareUrl(text);

  const handleClick = async (e) => {
    e.preventDefault();
    
    // Always open WhatsApp directly with the full pre-filled message.
    // navigator.share often only forwards the URL when the user picks WhatsApp,
    // so we bypass it and use the wa.me click-to-chat URL instead.
    const newWindow = window.open(whatsappUrl, "_blank");
    
    // If popup was blocked, copy the message to clipboard so user can paste it.
    if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
      try {
        await navigator.clipboard?.writeText(text);
        toast.success("WhatsApp didn't open — message copied to clipboard");
      } catch {
        // ignore
      }
    }
  };

  const variants = {
    primary: "bg-[#25D366] text-white hover:bg-[#20BD5A]",
    outline: "border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10",
    ghost: "bg-transparent text-[#25D366] hover:bg-[#25D366]/10",
  };
  const sizes = {
    sm: "h-9 px-3 text-xs rounded-xl",
    md: "h-12 px-4 text-[15px] rounded-2xl",
    icon: "h-9 w-9 rounded-full",
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold btn-press no-underline",
        variants[variant],
        sizes[size],
        className
      )}
      data-testid={testid}
      aria-label="Share on WhatsApp"
    >
      <Share2 size={size === "sm" ? 14 : size === "icon" ? 16 : 18} />
      {size !== "icon" && <span>{label}</span>}
    </a>
  );
}
